<script setup>
/**
 * MessageListSkeleton — o esqueleto da conversa enquanto as mensagens não chegam.
 *
 * Por que existe: no primeiro carregamento o painel de mensagens ficava BRANCO com um
 * spinner solto no meio, enquanto o cabeçalho e o compositor já estavam desenhados em
 * volta. Isso não lê como "carregando", lê como "quebrou" — a moldura inteira pronta em
 * volta de um buraco é a assinatura visual de erro, não de espera.
 *
 * O spinner que fazia esse papel não foi criado para ele: ele é o indicador de PAGINAÇÃO
 * (`isLoadingPrevious`), que vive no topo da lista e faz todo sentido ali. Reaproveitá-lo
 * para o carregamento inicial dava a ele uma segunda função e nenhuma das duas ficava clara.
 *
 * O esqueleto desenha a FORMA que vai chegar — bolhas alternadas entre entrada e saída,
 * larguras irregulares — porque um esqueleto que não prevê o layout final produz um salto
 * no momento em que o conteúdo entra, e o salto é exatamente o que ele deveria evitar.
 *
 * Sem animação de entrada de propósito: quem abre uma conversa está esperando conteúdo, e
 * uma animação aqui só adia o que ele veio ver. A pulsação (`animate-loader-pulse`, a mesma
 * que o resto do produto usa) já diz "vivo, aguarde" sem custar tempo percebido.
 */

import { useI18n } from 'vue-i18n';

const { t } = useI18n();

// Larguras fixas, não sorteadas: `Math.random()` mudaria o desenho a cada render e faria o
// esqueleto TREMER durante a espera. Alternância de lado embutida na lista.
const BOLHAS = [
  { entrada: true, largura: '58%' },
  { entrada: false, largura: '42%' },
  { entrada: true, largura: '71%' },
  { entrada: true, largura: '34%' },
  { entrada: false, largura: '63%' },
  { entrada: false, largura: '38%' },
  { entrada: true, largura: '49%' },
];
</script>

<template>
  <div
    class="flex flex-col justify-end gap-3 px-4 pb-4 h-full overflow-hidden"
    role="status"
    aria-live="polite"
    :aria-label="t('CONVERSATION.LOADING_CONVERSATIONS')"
  >
    <div
      v-for="(bolha, i) in BOLHAS"
      :key="i"
      class="flex w-full"
      :class="bolha.entrada ? 'justify-start' : 'justify-end'"
    >
      <div
        class="h-11 rounded-card bg-n-alpha-2 animate-loader-pulse"
        :style="{ width: bolha.largura, animationDelay: `${i * 90}ms` }"
      />
    </div>
  </div>
</template>
